import { useEffect, useState } from 'react'
import { GetServerSideProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled from 'styled-components'
import { pick } from 'ramda'
import { Box } from '@machobear/design-system'
import { useSelector } from 'react-redux'

import { FileFixedPosition } from '@/types'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import { getBuildingData } from '@/lib/building'
import { initializeStore, useStore } from '@/lib/redux'
import { getSession } from '@/lib/session'
import { getBuildingUrl, getLocalBuildingUrl } from '@/utils/building'
import { getImageUrl } from '@/utils/image'
import { isW1 } from '@/utils/listings'
import { makeMainLayout } from '@/layouts/main'
import { ReactComponent as MapPin } from '@/features/Building/images/pin.svg'
import Info from '@/features/Building/info'
import { ContentWrapper } from '@/features/Building/styles'
import Head from '@/components/head'
import { BuildingIcon, CarChargingIcon, ElevatorIcon } from '@/components/icons'
import ListingPhotos from '@/components/listing-photos'
import Map from '@/components/maplibre-map/map'
import { mapContainerForBuilding } from '@/components/maplibre-map/map/styles.css'
import Marker from '@/components/maplibre-map/marker'

const headerConfig = {
  showSearch: true
}
const footerConfig = {}
const supportConfig = {
  enabled: false
}

const getLocation = (data: { lat: string; lng: string }): [number, number] => [
  Number(data.lat),
  Number(data.lng)
]

const ContentContainer = styled.div``

const TitleContainer = styled.div`
  @media screen and (min-width: 800px) {
    margin-bottom: 40px;
  }
`

const Header = styled.div`
  background: ${colors.white};
  display: flex;

  & ${ContentWrapper} {
    display: flex;
    flex: 1;
    align-items: flex-start;
    margin: 30px auto 5px;
    height: auto;

    @media screen and (max-width: 800px) {
      flex-direction: column;
      align-items: flex-start;
    }

    @media screen and (max-width: ${breakpoints.phoneMax}) {
      margin-top: 15px;
    }
  }
`

const Type = styled.h4`
  color: ${colors.secondary};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  line-height: 18px;
  text-transform: uppercase;
  margin: 0 0 10px 0;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: ${fontSizes.small};
    margin: 0;
  }
`

const Title = styled.h1`
  color: ${colors.darkGrey};
  margin: 0;
  line-height: 0;
`

const BuildingName = styled.span`
  color: ${colors.regular};
  font-size: ${fontSizes.extraLarge};
  font-weight: 700;
  line-height: 24px;
  margin: 5px 0 20px 0;
  display: block;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: ${fontSizes.large};
    font-weight: 700;
  }
`

const Address = styled.span`
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.regular};
  letter-spacing: -0.32px;
  line-height: 14px;
  margin: 5px 0 0 0;
  display: block;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    display: none;
  }
`

const MainDetails = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;

  @media screen and (min-width: 850px) {
    margin-bottom: 20px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 10px 0 0 0;
    flex-shrink: 1;
    width: 100%;
    justify-content: flex-start;
  }
`

const ShadowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 65px;
  margin: 20px 20px 0 0;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 2px rgba(0, 0, 0, 0.12);
  z-index: 1;

  &:last-child {
    margin-right: 0;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 30%;
    max-width: 100px;
    margin-top: 0;
  }
`

const DetailText = styled.p`
  color: ${colors.regular};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.medium};
  margin: 2px 0 -2px 0;
`

const MobileAddress = styled.div`
  display: none;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    display: block;
    padding: 20px 20px 50px;

    .address {
      margin: 0;
      font-size: ${fontSizes.regular};
      font-weight: ${fontWeights.medium};
      letter-spacing: -0.32px;
      line-height: 20px;
    }

    .neighbourhood {
      margin: 5px 0 0 0;
      font-size: ${fontSizes.small};
      font-weight: normal;
      letter-spacing: -0.26pt;
      line-height: 18pt;
    }
  }
`

const MarkerWrapper = styled.div`
  position: absolute;
  top: -42px;
  left: -19px;
`

const Building = () => {
  const { t } = useTranslation()
  const store = useStore()
  const [width, setWidth] = useState(0)
  const building = useSelector(store.select.building.building)
  const { id, name, full_street_name, city, state, image } = building || {}

  useEffect(() => {
    function updateWindowDimensions() {
      setWidth(window.innerWidth)
    }

    updateWindowDimensions()
    window.addEventListener('resize', updateWindowDimensions)
    return () => {
      window.removeEventListener('resize', updateWindowDimensions)
    }
  }, [])

  const sliderSettings = (unitPhotos: FileFixedPosition[]) => {
    const showNavigation = unitPhotos.length > 1
    return {
      dots: showNavigation,
      infinite: true,
      arrows: showNavigation,
      className: 'center',
      centerMode: true,
      centerPadding: `${(width - 750) / 2}px`,
      swipeToSlide: showNavigation,
      swipe: showNavigation,
      initialSlide: 0,
      slidesToShow: 1,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 1,
            initialSlide: 0,
            centerMode: false
          }
        },
        {
          breakpoint: 600,
          settings: {
            slidesToShow: 1,
            initialSlide: 0,
            centerMode: false
          }
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            initialSlide: 0,
            centerMode: false
          }
        }
      ]
    }
  }

  const renderCarousel = () => {
    if (!building || !building.image.length) return null
    return (
      <ListingPhotos
        listings={undefined}
        images={building.image}
        sliderSettings={sliderSettings(building.image)}
        altText={`${name} Apartments for Rent, ${full_street_name}, ${city}, ${state} -`}
        buttonText={t(
          'building:b.building.view.view_image.button',
          'View Image'
        )}
      />
    )
  }

  const renderHead = () => {
    if (!building) return null
    const pagePath = getBuildingUrl(building)
    const metaImage = getImageUrl(image[0]?.aws_s3_key, {
      fit: 'fit-in/1200x630',
      quality: 100
    })

    if (isW1(id)) {
      const w1MetaTitle = t(
        'building:b.building.view.w1.title',
        'W1 Vancouver Luxury Rental Apartments | liv.rent'
      )
      const w1MetaDescription = t(
        'building:b.building.view.w1.description',
        'Located in the heart of The Cambie Marine Hub. W1 is a collection of 1, 2, and 3 bedroom luxury furnished and unfurnished pet-friendly apartments for rent in Vancouver. Available for executive and corporate long and short-term stays.'
      )
      return (
        <Head
          title={w1MetaTitle}
          description={w1MetaDescription}
          meta={{ image: metaImage, imageAlt: `${name} ${full_street_name}` }}
          path={pagePath}
        />
      )
    }

    const metaTitle = t('building:b.building.view.meta_title.text', {
      defaultValue:
        '{{ name }} {{ city }}, {{ state }} - {{ full_street_name }} | liv.rent',
      name,
      full_street_name,
      city,
      state
    })
    const metaDescription = t(
      'building:b.building.view.meta_description.text',
      {
        defaultValue: `View apartments for rent at {{ name }}, {{ full_street_name }}, {{ city }}, {{ state }}. Check out amenities and condo units in {{ name }}`,
        name,
        full_street_name,
        city,
        state
      }
    )

    return (
      <Head
        title={metaTitle}
        description={metaDescription}
        meta={{
          image: metaImage,
          imageAlt: `${name} ${full_street_name}`
        }}
        path={pagePath}
      />
    )
  }

  const MarkerComponent = () => {
    return (
      <MarkerWrapper>
        <MapPin height="50px" width="50px" />
      </MarkerWrapper>
    )
  }

  const renderMap = () => {
    if (!building) return null
    if (!building.geo_address) return null
    return (
      <Box width="full" className={mapContainerForBuilding}>
        <Map center={getLocation(building)} zoom={14}>
          <Marker
            position={getLocation(building)}
            IconComponent={MarkerComponent}
          />
        </Map>
      </Box>
    )
  }

  const renderMobileAddress = () => {
    if (!building) return null
    return (
      <MobileAddress>
        <h2 className="address">{building.full_street_name}</h2>
        <h2 className="address">
          {building.city}, {building.zip}, {building.state}
        </h2>
        {building.neighborhood && (
          <p className="neighbourhood">{building.neighborhood}</p>
        )}
      </MobileAddress>
    )
  }
  const renderBuilding = () => {
    if (!building) return null
    return (
      <ContentContainer>
        {renderBuildingHeader()}
        <Info building={building} />
      </ContentContainer>
    )
  }
  const renderBuildingHeader = () => {
    return (
      <Header>
        <ContentWrapper>
          <TitleContainer>
            <Type>
              {t(
                'building:b.building.view.building_info.text',
                'Building Info'
              )}
            </Type>
            <Title>
              <BuildingName>{building.name} </BuildingName>
              <Address>{building.full_street_name} </Address>
              <Address>
                {building.city}, {building.state} {building.zip}
              </Address>
            </Title>
          </TitleContainer>
          <MainDetails>
            <ShadowWrapper>
              <BuildingIcon height={28} width={28} />
              <DetailText>
                {building.count_units}{' '}
                {t('building:b.building.view.units.text', 'Units')}
              </DetailText>
            </ShadowWrapper>
            <ShadowWrapper>
              <ElevatorIcon height={28} width={28} />
              <DetailText>
                {building.count_stories}{' '}
                {t('building:b.building.view.floors.text', 'Floors')}
              </DetailText>
            </ShadowWrapper>
            {building.ev_ready && (
              <ShadowWrapper>
                <CarChargingIcon height={28} width={28} />
                <DetailText>
                  {t('building:b.building.view.ev.text', 'EV')}
                </DetailText>
              </ShadowWrapper>
            )}
          </MainDetails>
        </ContentWrapper>
      </Header>
    )
  }

  return (
    <>
      {renderHead()}
      {renderCarousel()}
      {renderBuilding()}
      {renderMap()}
      {renderMobileAddress()}
    </>
  )
}

const selectFromState = pick(['building', 'session'])

export const getServerSideProps: GetServerSideProps = async ({
  req,
  query,
  locale
}) => {
  const reduxStore = initializeStore()
  await Promise.all([
    getBuildingData({
      reduxStore,
      query,
      req
    }),
    getSession({ req, reduxStore })
  ])
  const reduxState = reduxStore.getState()
  const building = reduxState?.building?.building

  if (!building)
    return {
      notFound: true
    }
  // Redirect old building URLs to new ones
  const prefix = locale && locale !== 'en' ? `/${locale}` : ''
  const currentDestination = `${prefix}/building/${query?.id}/${query?.name}`
  const desiredDestination = getLocalBuildingUrl(building, locale)
  if (currentDestination !== desiredDestination)
    return {
      redirect: {
        permanent: true,
        destination: desiredDestination
      }
    }
  let translations = {}
  if (locale) {
    translations = await serverSideTranslations(locale, [
      'translation',
      'basic',
      'building'
    ])
  }
  return {
    props: {
      initialReduxState: selectFromState(reduxStore.getState()),
      ...translations
    }
  }
}

Building.Layout = makeMainLayout({
  headerConfig,
  footerConfig,
  supportConfig
})

export default Building
